<template>
  <div class="tournament-detail">
    <div class="back-link" @click="$router.push('/tournaments')">
      <span>{{t('tournament.backToTournaments')}}</span>
      <i class="mdi mdi-arrow-left"></i>
    </div>
    <div class="card">
      <section class="header">
        <div class="image-container">
          <img :src="tournament.image" :alt="tournament.title" />
        </div>
        <div class="info">
          <div class="row">
            <div class="col-12 col-lg-10">
              <h2>
                <i style="color: #df824d" :class="['mdi', getTournamentIcon]"></i>
                {{ tournament.title }}
              </h2>
              <p class="description">{{ tournament.description }}</p>
          
              <div class="meta">
                <div class="meta-item">
                  <i class="mdi mdi-calendar"></i>
                  <span>{{ tournament.date }}</span>
                </div>
                <div class="meta-item">
                  <i class="mdi mdi-account-group"></i>
                  <span>{{ tournament.maxParticipants }}</span>
                </div>
                <div class="meta-item">
                  <i class="mdi mdi-trophy"></i>
                  <span>{{ tournament.prizePool }}</span>
                </div>
              </div>
            </div>
            <div class="col-lg-2 button-row">
              <a v-if="tournament.urlSignUp !== 'none'" href="https://docs.google.com/forms/d/e/1FAIpQLSdqUFfVB5IJUqZ7Q-I6phn7fltm0nRpZxfFAuGJGPq4Yy5JxA/viewform?usp=dialog" target="_blank" rel="noopener noreferrer" class="action-button">
                {{t('tournament.signUp')}}
              </a>
              <router-link to="/information/tournaments" class="action-button">
                {{t('tournament.generalRules')}}
              </router-link>
            </div>
          </div>
        </div>
      </section>

      <section class="content">
        <div class="row">
          <div class="col">
            <div class="prizes" v-if="tournament.prizes">
              <h3>🏅 {{t('tournament.prizes')}}</h3>
              <ul>
                <li v-for="(prize, i) in tournament.prizes" :key="i">
                  <template v-if="getPrizeUrl(prize)">
                    <a :href="getPrizeUrl(prize)" target="_blank" rel="noopener noreferrer" class="prize-link">{{ prize }}</a>
                  </template>
                  <template v-else>
                    {{ prize }}
                  </template>
                </li>
              </ul>
            </div>
          </div>
          <div class="col">
            <div class="schedule" v-if="tournament.schedule">
              <h3>🕐 {{t('tournament.schedule')}}</h3>
              <ul>
                <li v-for="(schedule, i) in tournament.schedule" :key="i">
                  {{ schedule }}
                </li>
              </ul>
            </div>
          </div>
        </div>

        <section class="rules-section">
          <div v-if="tournament.maps && tournament.maps.length > 0">
              <h3>Maps</h3>
              <div class="maps-grid">
                <a v-for="(map, index) in tournament.maps" 
                   :key="'maps-'+ index" 
                   :href="map.split('?')[0]"
                   target="_blank"
                   rel="noopener noreferrer"
                   class="map-card"
                   :style="{ backgroundImage: `url(${map.split('?')[0].replace('/mapshow/', '/mapthumb/')})` }">
                  <div class="map-overlay"></div>
                  <div class="map-title">{{ getMapName(map) }}</div>
                </a>
              </div>
          </div>
          <h2>{{t('tournament.tournamentRules')}}</h2>
          <div class="rules-grid">
            <div v-if="tournament.rules && tournament.rules.length > 0" class="rules-card">
              <h3>{{t('tournament.generalRules')}}</h3>
              <ul>
                <li v-for="(rule, index) in tournament.rules" :key="'rule-' + index">
                  {{ rule }}
                </li>
              </ul>
            </div>
            <div v-if="tournament.disclaimer && tournament.disclaimer.length > 0" class="rules-card">
              <h3>{{t('tournament.disclaimer')}}</h3>
              <ul>
                <li v-for="(disclaimer, index) in tournament.disclaimer" :key="'disclaimer-' + index">
                  {{ disclaimer }}
                </li>
              </ul>
            </div>
            <div v-if="tournament.inGame && tournament.inGame.length > 0" class="rules-card">
              <h3>{{t('tournament.inGameRules')}}</h3>
              <ul>
                <li v-for="(rule, index) in tournament.inGame" :key="'ingame-' + index">
                  {{ rule }}
                </li>
              </ul>
            </div>
            <div v-if="tournament.customization && tournament.customization.length > 0" class="rules-card">
              <h3>{{t('tournament.customRules')}}</h3>
              <ul>
                <li v-for="(rule, index) in tournament.customization" :key="'custom-' + index">
                  {{ rule }}
                </li>
              </ul>
            </div>
            <div v-if="tournament.gameclient && tournament.gameclient.length > 0" class="rules-card">
              <h3>{{t('tournament.gameClientRules')}}</h3>
              <ul>
                <li v-for="(rule, index) in tournament.gameclient" :key="'client-' + index">
                  {{ rule }}
                </li>
              </ul>
            </div>
          </div>
        </section>

        <section v-if="tournament.scoring && tournament.scoring.length > 0" class="rules-section">
          <div class="rules-grid scoring-grid">
            <div class="rules-card scoring-card">
              <h3 class="">{{t('tournament.scoringRules')}}</h3>  
              <ul>
                <li v-for="(rule, index) in tournament.scoring" :key="'scoring-' + index">
                  {{ rule }}
                </li>
              </ul>
            </div>
          </div>
        </section>

        <div v-if="tournament.urlBracket !== 'none'" class="row d-flex flex-column mt-5 bracket">
          <h3 class="bracket-title">{{t('tournament.bracket')}}</h3>
          <iframe :src="tournament.urlBracket" width="100%" height="500" frameborder="0" scrolling="auto" allowtransparency="true"></iframe>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router';

import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const router = useRouter();

interface Tournament {
  id: number;
  title: string;
  date: string;
  description: string;
  image: string;
  maxParticipants: string;
  prizePool: string;
  category: 'prime' | 'core' | 'solo' | 'fun';
  maps?: string[];
  rules?: string[];
  disclaimer?: string[];
  inGame?: string[];
  customization?: string[];
  gameclient?: string[];
  schedule?: string[];
  registrationOpen: boolean;
  prizes?: string[];
  startTime?: string;
  endTime?: string;
  urlSignUp?: string;
  urlBracket?: string;
  urlRules?: string;
  scoring?: string[];
}

const props = defineProps<{
  tournament: Tournament
}>();

console.log(props.tournament.rules);

const prizeUrls = [
  {
    prizeName: "Sharkoon 1337 RGB V2 Gaming Mat 360",
    url: "https://www.alternate.be/Sharkoon/1337-RGB-V2-Gaming-Mat-360/html/product/1628178"
  },
  {
    prizeName: "Sharkoon 1337 V2 Gaming Mat XXL",
    url: "https://www.alternate.be/Sharkoon/1337-V2-Gaming-Mat-XXL/html/product/1628172?partner=B2CBESegmentify&_sgm_campaign=product&_sgm_source=1628172%7Ccategory&_sgm_action=search&_sgm_term=Sharkoon+1337+V2+Gaming+Mat+XXL&_sgm_pinned=false"
  },
  {
    prizeName: "be quiet! PURE BASE 501 Airflow Window midi tower case",
    url: "https://www.alternate.be/be-quiet/PURE-BASE-501-Airflow-Window-midi-tower-behuizing/html/product/100074439"
  },
  {
    prizeName: "Nintendo Switch",
    url: "https://www.alternate.be/Nintendo/Switch-spelconsole/html/product/1879851"
  }
];

const getPrizeUrl = (prize: string) => {
  const matchingPrize = prizeUrls.find(p => prize.includes(p.prizeName));
  return matchingPrize?.url;
};

const getMapName = (map: string) => {
  const urlParts = map.split('=');
  return urlParts[urlParts.length - 1];
};

const getTournamentIcon = computed(() => {
  const iconMap = {
    prime: 'mdi-alpha-p-box',
    core: 'mdi-alpha-c-box',
    solo: 'mdi-alpha-s-box',
    fun: 'mdi-alpha-f-box'
  };
  return iconMap[props.tournament.category] || 'mdi-gamepad-variant';
});
</script>

<style scoped>
.tournament-detail {
  width: 95%;
  overflow-x: hidden;
  padding: 2rem 0;
  margin: 0 auto;
  margin-top: 3rem;
  position: relative;
}

.back-link {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 2rem;
  cursor: pointer;
  color: #fff;
}

.back-link span {
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.back-link i {
  font-size: 1.5rem;
  color: #df824d;
}

.back-link:hover {
  opacity: 0.8;
}

.card {
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
  background: rgba(0, 0, 0, 0.5);
  border: 1px solid #ffffff1a;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s, box-shadow 0.2s;
  position: relative;
}

.card:hover {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
}

.header {
  display: flex;
  gap: 2rem;
  margin-bottom: 2rem;
}

.image-container {
  flex: 0 0 300px;
  height: 200px;
}

.image-container img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
}

.info {
  flex: 1;
}

h2 {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin: 0 0 1rem;
  color: #ffffff;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 2rem;
}

h2 i {
  font-size: 2rem;
  opacity: 0.8;
}

.description {
  margin-bottom: 1.5rem;
  color: #e0e0e0;
  line-height: 1.6;
}

.meta {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #e0e0e0;
}

.meta-item i {
  color: #df824d;
}

.content {
  padding: 0 2rem;
}

.row {
  display: flex;
  gap: 2rem;
  margin-bottom: 2rem;
  width: 100%;
}

.col {
  flex: 1;
}

h3 {
  color: #ffffff;
  margin-bottom: 1rem;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 1.5rem;
}

.bracket-title {
  color: #ffffff;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #76c4ae;
}

.rules-section {
  margin: 4rem 0;
}

.rules-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin-top: 2rem;
}

.rules-card {
  background: rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 4px;
  padding: 2rem;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.rules-card:hover {
  transform: translateY(-2px);
  box-shadow: rgba(118, 196, 174, 0.3) -10px 10px, rgba(118, 196, 174, 0.2) -20px 20px !important;
}

.rules-card h3 {
  color: #76c4ae;
  margin-bottom: 1.5rem;
  font-size: 1.2rem;
}

.rules-card ul {
  list-style-type: none;
  padding: 0;
}

.rules-card li {
  margin-bottom: 1rem;
  line-height: 1.6;
  position: relative;
  padding-left: 1.5rem;
}

.rules-card li::before {
  content: "•";
  color: #76c4ae;
  position: absolute;
  left: 0;
}

.maps-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 2rem;
  margin-top: 2rem;
  margin-bottom: 2rem;
}

.map-card {
  background: rgba(0, 0, 0, 0.5);
  border: 1px solid #76c4ae;
  backdrop-filter: blur(10px);
  border-radius: 4px;
  padding: 1rem;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  height: 150px;
  background-size: cover;
  background-position: center;
  overflow: hidden;
  text-decoration: none;
}

.map-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  transition: background 0.3s ease;
}

.map-card:hover {
  transform: translateY(-2px);
  box-shadow: rgba(118, 196, 174, 0.3) -10px 10px, rgba(118, 196, 174, 0.2) -20px 20px !important;
}

.map-card:hover .map-overlay {
  background: rgba(0, 0, 0, 0.3);
}

.map-title {
  position: relative;
  z-index: 1;
  color: #ffffff;
  font-size: 1.1rem;
  text-align: center;
  padding: 0 1rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
}

.scoring-grid {
  grid-template-columns: 1fr;
  max-width: 800px;
  margin: 2rem auto;
}

.scoring-card {
  text-align: center;
}

.scoring-card ul {
  display: inline-block;
  text-align: left;
}

.button-row {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2rem;
  margin-top: 2rem;
  padding: 0;
}

.action-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: #df824d;
  color: white;
  text-decoration: none;
  border-radius: 4px;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 2px;
  font-size: 0.8rem;
  font-weight: 500;
  min-width: 200px;
  max-height: 50px;
  justify-content: center;
}

.action-button:hover {
  background: #e69b71;
  transform: translateY(-2px);
}

.action-button i {
  font-size: 1.2rem;
}

.prize-link {
  color: #e69b71;
  text-decoration: none;
  transition: color 0.2s;
}

.prize-link:hover {
  color: #ff9f5f;
  text-decoration: underline;
}


@media (max-width: 1056px) {
   .header {
    flex-direction: column;
   }

   .maps-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
}

@media (max-width: 768px) {

  .card {
    padding: 1rem;
  }

  .content {
    padding: 0;
  }

  .image-container {
    flex: 0 0 auto;
    height: 200px;
  }

  .row {
    flex-direction: column;
  }

  .rules-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .map-card {
    padding: 0.75rem;
  }

  .map-card img {
    height: 120px;
  }

  .map-title {
    font-size: 1rem;
  }
  
  .rules-card {
    padding: 1.5rem;
  }
}
</style>
